import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/api';
import { useHapticFeedback } from '../hooks/useTelegram';
import './SBPLinkingPage.css';
import './LinkTelegramPage.css';

interface LinkTelegramPageProps {
  /** Called after the user chose to continue as new (either "Нет" or "not_found" result).
   *  Parent should refresh auth state so the gate releases. */
  onDismissed: () => void;
  /** Called after a successful link. Parent should refresh auth so the verified user is loaded. */
  onLinked: () => void;
}

type LinkStep =
  | 'intro'
  | 'form'
  | 'submitting'
  | 'code'
  | 'verifying'
  | 'not_found'
  | 'linked';

export const LinkTelegramPage: React.FC<LinkTelegramPageProps> = ({ onDismissed, onLinked }) => {
  const { notification } = useHapticFeedback();

  const [step, setStep] = useState<LinkStep>('intro');
  const [phone, setPhone] = useState('+7 ');
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [error, setError] = useState('');
  const [dismissing, setDismissing] = useState(false);

  const [smsCode, setSmsCode] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockCountdown, setLockCountdown] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(180);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
  const proxyInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      if (lockTimerRef.current) clearInterval(lockTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (step === 'code' && resendCountdown > 0) {
      resendTimerRef.current = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            if (resendTimerRef.current) clearInterval(resendTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => {
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      };
    }
  }, [step, resendCountdown > 0 ? 'active' : 'inactive']);

  useEffect(() => {
    if (lockedUntil) {
      const tick = () => {
        const remaining = Math.max(0, Math.ceil((lockedUntil * 1000 - Date.now()) / 1000));
        setLockCountdown(remaining);
        if (remaining <= 0) {
          setLockedUntil(null);
          setAttemptsLeft(3);
          if (lockTimerRef.current) clearInterval(lockTimerRef.current);
        }
      };
      tick();
      lockTimerRef.current = setInterval(tick, 1000);
      return () => {
        if (lockTimerRef.current) clearInterval(lockTimerRef.current);
      };
    }
  }, [lockedUntil]);

  useEffect(() => {
    if (step === 'code') {
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 50);
    }
  }, [step]);

  const formatPhoneBody = (digits: string): string => {
    const d = digits.slice(0, 10);
    if (d.length === 0) return '';
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    if (d.length <= 8) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const allDigits = raw.replace(/\D/g, '');
    let body: string;
    if (allDigits.startsWith('7') || allDigits.startsWith('8')) {
      body = allDigits.slice(1);
    } else {
      body = allDigits;
    }
    const formatted = formatPhoneBody(body);
    setPhone(formatted ? `+7 ${formatted}` : '+7 ');
    setError('');
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    if (e.key === 'Backspace' && (input.selectionStart ?? 0) <= 3) {
      e.preventDefault();
    }
  };

  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;
    setTimeout(() => {
      input.selectionStart = input.selectionEnd = input.value.length;
    }, 0);
  };

  const getPhoneDigits = (): string => {
    const allDigits = phone.replace(/\D/g, '');
    return allDigits.startsWith('7') ? allDigits.slice(1) : allDigits;
  };

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatLockTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h} ч ${m} мин`;
    return `${m} мин ${s} сек`;
  };

  const handleChooseYes = () => {
    setStep('form');
    setError('');
  };

  const handleChooseNo = useCallback(async () => {
    if (dismissing) return;
    try {
      setDismissing(true);
      await apiClient.dismissMaxTgLinkPrompt();
    } catch (err) {
      console.error('Failed to dismiss TG link prompt:', err);
    } finally {
      setDismissing(false);
      onDismissed();
    }
  }, [dismissing, onDismissed]);

  const handleSubmitPhone = useCallback(async () => {
    if (getPhoneDigits().length < 10) {
      setError('Введите полный номер телефона');
      return;
    }

    proxyInputRef.current?.focus();

    setStep('submitting');
    setError('');

    try {
      const digits = getPhoneDigits();
      await apiClient.sendMaxTgLinkCode(digits);

      setStep('code');
      setSmsCode('');
      setAttemptsLeft(3);
      setResendCountdown(180);
    } catch (err: any) {
      console.error('Failed to send TG link code:', err);
      const responseData = err?.response?.data;

      if (err?.response?.status === 429 && responseData?.locked_until) {
        setLockedUntil(responseData.locked_until);
        setStep('code');
        setAttemptsLeft(0);
        return;
      }

      const message = responseData?.message || 'Не удалось отправить код. Попробуйте позже.';
      setError(message);
      setStep('form');
      notification('error');
    }
  }, [phone, notification]);

  const handleVerifyCode = useCallback(async () => {
    if (smsCode.length !== 4) {
      setError('Введите 4-значный код');
      return;
    }

    setStep('verifying');
    setError('');

    try {
      const digits = getPhoneDigits();
      const result = await apiClient.verifyMaxTgLinkCode(digits, smsCode);

      if (result.status === 'linked') {
        setStep('linked');
        notification('success');
        setTimeout(() => {
          onLinked();
        }, 1500);
        return;
      }

      if (result.status === 'not_found') {
        setStep('not_found');
        notification('warning');
        return;
      }
    } catch (err: any) {
      console.error('Failed to verify TG link code:', err);
      const responseData = err?.response?.data;

      if (err?.response?.status === 429 && responseData?.locked_until) {
        setLockedUntil(responseData.locked_until);
        setAttemptsLeft(0);
        setError(responseData.message || 'Слишком много попыток');
        setStep('code');
        notification('error');
        return;
      }

      if (responseData?.attempts_left !== undefined) {
        setAttemptsLeft(responseData.attempts_left);
      }

      setError(responseData?.message || 'Ошибка проверки кода');
      setStep('code');
      setSmsCode('');
      notification('error');
    }
  }, [smsCode, phone, notification, onLinked]);

  const handleResendCode = useCallback(async () => {
    if (resendCountdown > 0) return;

    try {
      const digits = getPhoneDigits();
      const result = await apiClient.resendMaxTgLinkCode(digits);
      if (result.success) {
        setResendCountdown(result.resend_cooldown_seconds || 180);
        setAttemptsLeft(3);
        setError('');
        setSmsCode('');
        notification('success');
      }
    } catch (err: any) {
      const responseData = err?.response?.data;
      if (responseData?.remaining_seconds) {
        setResendCountdown(responseData.remaining_seconds);
      }
      console.error('Failed to resend TG link code:', err);
    }
  }, [resendCountdown, notification, phone]);

  const handleDigitChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '');

    if (digits.length > 1) {
      const code = digits.slice(0, 4);
      setSmsCode(code);
      setError('');
      const focusIndex = Math.min(code.length, 3);
      codeInputRefs.current[focusIndex]?.focus();
      return;
    }

    const digit = digits.slice(-1);
    const newCode = smsCode.split('');
    newCode[index] = digit;
    const joined = newCode.join('').slice(0, 4);
    setSmsCode(joined);
    setError('');
    if (digit && index < 3) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !smsCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && smsCode.length === 4) {
      handleVerifyCode();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted) {
      setSmsCode(pasted);
      setError('');
      const focusIndex = Math.min(pasted.length, 3);
      codeInputRefs.current[focusIndex]?.focus();
    }
  };

  const handleBackToIntro = () => {
    setStep('intro');
    setError('');
    setSmsCode('');
    setPhone('+7 ');
  };

  const handleBackToForm = () => {
    setStep('form');
    setError('');
    setSmsCode('');
  };

  const title =
    step === 'intro'
      ? 'Привязка Telegram'
      : step === 'not_found'
      ? 'Аккаунт не найден'
      : step === 'linked'
      ? 'Аккаунт найден'
      : 'Вход через Telegram';

  return (
    <div className="sbp-linking-page">
      <input
        ref={proxyInputRef}
        inputMode="numeric"
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'fixed', top: '-9999px', opacity: 0, pointerEvents: 'none' }}
        readOnly
      />
      <div className="sbp-linking-header">
        <h1 className="sbp-linking-title">{title}</h1>
      </div>

      <div className="sbp-linking-content">
        {step === 'intro' && (
          <div className="link-tg-intro">
            <p className="sbp-linking-description link-tg-intro-question">
              У вас уже есть аккаунт в Telegram?
            </p>
            <p className="link-tg-intro-hint">
              Если да — привяжем его по номеру телефона, чтобы вы продолжили с вашего уже существующего профиля.
            </p>

            {error && <span className="sbp-input-error">{error}</span>}

            <div className="sbp-form-actions">
              <button
                className="sbp-submit-button button--accent"
                onClick={handleChooseYes}
                disabled={dismissing}
              >
                Да, есть
              </button>

              <button
                className="sbp-back-button-secondary"
                onClick={handleChooseNo}
                disabled={dismissing}
              >
                {dismissing ? 'Сохраняем...' : 'Нет аккаунта, я здесь впервые'}
              </button>
            </div>
          </div>
        )}

        {step === 'form' && (
          <>
            <p className="sbp-linking-description">
              Введите номер телефона, который привязан к вашему Telegram-аккаунту в приложении
            </p>

            <div className="sbp-input-group">
              <input
                type="tel"
                className={`sbp-phone-input ${error ? 'sbp-phone-input-error' : ''}`}
                placeholder="+7 (___) ___-__-__"
                value={phone}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                onFocus={(e) => { handlePhoneFocus(e); setIsPhoneFocused(true); }}
                onBlur={() => setIsPhoneFocused(false)}
              />
            </div>

            {error && <span className="sbp-input-error">{error}</span>}

            <div className={`sbp-form-actions ${isPhoneFocused ? 'sbp-form-actions-keyboard' : ''}`}>
              <button
                className="sbp-submit-button button--accent"
                onClick={handleSubmitPhone}
                disabled={getPhoneDigits().length < 10}
              >
                Получить код
              </button>

              <button
                className="sbp-back-button-secondary"
                onClick={handleBackToIntro}
              >
                Назад
              </button>
            </div>
          </>
        )}

        {step === 'submitting' && (
          <div className="sbp-linking-status-section">
            <div className="sbp-progress-spinner" />
            <p className="sbp-progress-text">Отправка кода...</p>
          </div>
        )}

        {step === 'code' && (
          <div className="sbp-code-section">
            <h2 className="sbp-code-title">Введите код из SMS</h2>
            <p className="sbp-code-description">Мы отправили код подтверждения на номер</p>
            <p className="sbp-code-phone">{phone}</p>

            {lockedUntil && lockCountdown > 0 ? (
              <div className="sbp-code-locked">
                <div className="sbp-code-locked-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="#EF4444" opacity="0.15"/>
                    <path d="M18 22V18C18 14.69 20.69 12 24 12C27.31 12 30 14.69 30 18V22" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                    <rect x="16" y="22" width="16" height="14" rx="2" fill="#EF4444" opacity="0.3" stroke="#EF4444" strokeWidth="2"/>
                    <circle cx="24" cy="29" r="2" fill="#EF4444"/>
                  </svg>
                </div>
                <p className="sbp-code-locked-text">Превышено количество попыток</p>
                <p className="sbp-code-locked-timer">
                  Повторите через {formatLockTime(lockCountdown)}
                </p>
              </div>
            ) : (
              <>
                <div className="sbp-code-input-group">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={`sbp-code-digit-wrapper ${error ? 'sbp-code-digit-error' : ''}`}>
                      <input
                        ref={(el) => { codeInputRefs.current[i] = el; }}
                        type="text"
                        className="sbp-code-digit"
                        value={smsCode[i] || ''}
                        onChange={(e) => handleDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleDigitKeyDown(i, e)}
                        onPaste={handleDigitPaste}
                        maxLength={i === 0 ? undefined : 1}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        autoFocus={i === 0}
                      />
                    </div>
                  ))}
                </div>

                {error && attemptsLeft > 0 && (
                  <span className="sbp-code-error">
                    Неверный код: осталось {attemptsLeft} {attemptsLeft === 1 ? 'попытка' : 'попытки'}
                  </span>
                )}
                {error && attemptsLeft === 0 && (
                  <span className="sbp-code-error">{error}</span>
                )}

                <div className="sbp-code-resend">
                  {resendCountdown > 0 ? (
                    <span className="sbp-code-resend-timer">
                      Отправить новый код через {formatTime(resendCountdown)}
                    </span>
                  ) : (
                    <button
                      className="sbp-code-resend-button"
                      onClick={handleResendCode}
                    >
                      Отправить новый код
                    </button>
                  )}
                </div>

                <button
                  className="sbp-submit-button button--accent"
                  onClick={handleVerifyCode}
                  disabled={smsCode.length !== 4}
                >
                  Подтвердить
                </button>
              </>
            )}

            {!(lockedUntil && lockCountdown > 0) && (
              <button
                className="sbp-back-button-secondary"
                onClick={handleBackToForm}
              >
                Изменить номер
              </button>
            )}
          </div>
        )}

        {step === 'verifying' && (
          <div className="sbp-linking-status-section">
            <div className="sbp-progress-spinner" />
            <p className="sbp-progress-text">Проверяем код...</p>
          </div>
        )}

        {step === 'linked' && (
          <div className="sbp-linking-status-section">
            <div className="sbp-success-icon">
              <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="30" fill="#DCFCE7"/>
                <circle cx="30" cy="30" r="12.5" stroke="#22C55E" strokeWidth="1.5" fill="none"/>
                <path d="M24 30L28.5 34.5L36 25.5" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="sbp-success-text">Аккаунт найден</p>
            <p className="sbp-success-description">Загружаем ваш профиль...</p>
          </div>
        )}

        {step === 'not_found' && (
          <>
            <div className="sbp-linking-status-section">
              <div className="sbp-error-icon">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="30" cy="30" r="30" fill="#FEE2E2"/>
                  <circle cx="30" cy="30" r="12.5" stroke="#EF4444" strokeWidth="1.5" fill="none"/>
                  <path d="M30 24V32" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="30" cy="36" r="1.2" fill="#EF4444"/>
                </svg>
              </div>
              <p className="sbp-error-text">Аккаунт не найден</p>
              <p className="sbp-error-description">
                Аккаунт с таким номером не найден. Вы можете продолжить как новый пользователь.
              </p>
            </div>

            <div className="sbp-form-actions">
              <button
                className="sbp-submit-button button--accent"
                onClick={onDismissed}
              >
                Продолжить
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LinkTelegramPage;
